import { SuggestionItem } from '../types';

export const CLUSTER_INFO_SUGGESTIONS: SuggestionItem[] = [
  {
    id: 'cluster.esHealth',
    label: 'ES Health',
    description: 'Elasticsearch/OpenSearch cluster health status (green/yellow/red)',
    keywords: ['es health', 'cluster health', 'health status', 'cluster status', 'green', 'red', 'yellow'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.es60.cluster.health.status',
    formatter: (v) => `**ES Health:** ${v || 'unknown'}`,
  },
  {
    id: 'cluster.kibanaHealth',
    label: 'Kibana Health',
    description: 'Kibana/Dashboards health status',
    keywords: ['kibana', 'kibana health', 'dashboards health'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.kibana.health',
    formatter: (v) => `**Kibana Health:** ${v || 'unknown'}`,
  },
  {
    id: 'cluster.serverType',
    label: 'Server Type',
    description: 'Server type of the cluster',
    keywords: ['server type', 'server'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.clusterData.serverType',
    formatter: (v) => `**Server Type:** ${v || 'N/A'}`,
  },
  {
    id: 'cluster.discoveryMode',
    label: 'Discovery Mode',
    description: 'Cluster discovery mode',
    keywords: ['discovery', 'discovery mode'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.clusterData.discoveryMode',
    formatter: (v) => `**Discovery Mode:** ${v || 'N/A'}`,
  },
  {
    id: 'cluster.activeShards',
    label: 'Active Shards',
    description: 'Number of active shards in the cluster',
    keywords: ['active shards', 'shards', 'shard count'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.es60.cluster.health.active_shards',
    formatter: (v) => `**Active Shards:** ${v ?? 'N/A'}`,
  },
  {
    id: 'cluster.activePrimaryShards',
    label: 'Active Primary Shards',
    description: 'Number of active primary shards',
    keywords: ['primary shards', 'active primary'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.es60.cluster.health.active_primary_shards',
    formatter: (v) => `**Active Primary Shards:** ${v ?? 'N/A'}`,
  },
  {
    id: 'cluster.relocatingShards',
    label: 'Relocating Shards',
    description: 'Number of shards currently relocating',
    keywords: ['relocating', 'relocating shards'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.es60.cluster.health.relocating_shards',
    formatter: (v) => `**Relocating Shards:** ${v ?? 'N/A'}`,
  },
  {
    id: 'cluster.initializingShards',
    label: 'Initializing Shards',
    description: 'Number of shards currently initializing',
    keywords: ['initializing', 'initializing shards'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.es60.cluster.health.initializing_shards',
    formatter: (v) => `**Initializing Shards:** ${v ?? 'N/A'}`,
  },
  {
    id: 'cluster.unassignedShards',
    label: 'Unassigned Shards',
    description: 'Number of unassigned shards',
    keywords: ['unassigned', 'unassigned shards'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.es60.cluster.health.unassigned_shards',
    formatter: (v) => `**Unassigned Shards:** ${v ?? 'N/A'}`,
  },
  {
    id: 'cluster.dataNodes',
    label: 'Data Nodes Count',
    description: 'Number of data nodes in the cluster',
    keywords: ['data nodes', 'number of data nodes', 'data node count'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.es60.cluster.health.number_of_data_nodes',
    formatter: (v) => `**Data Nodes:** ${v ?? 'N/A'}`,
  },
  {
    id: 'cluster.masterNodes',
    label: 'Master Nodes Count',
    description: 'Number of master nodes',
    keywords: ['master nodes', 'master', 'dedicated master'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.es60.cluster.stats.nodes.count.master',
    formatter: (v) => `**Master Nodes:** ${v ?? 'N/A'}`,
  },
  {
    id: 'cluster.coordinatorNodes',
    label: 'Coordinator Nodes Count',
    description: 'Number of coordinating-only nodes',
    keywords: ['coordinator', 'coordinating', 'coordinator nodes'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.es60.cluster.stats.nodes.count.coordinating_only',
    formatter: (v) => `**Coordinator Nodes:** ${v ?? 'N/A'}`,
  },
  {
    id: 'cluster.disk',
    label: 'Disk Usage',
    description: 'Cluster disk usage (free / total)',
    keywords: ['disk', 'disk usage', 'storage', 'free disk', 'disk space'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.es60.cluster.stats.nodes.fs',
    formatter: (v) => {
      if (!v) return '**Disk Usage:** N/A';
      return `**Disk Usage:** ${v.available || 'N/A'} free / ${v.total || 'N/A'} total`;
    },
  },
  {
    id: 'cluster.pendingTasks',
    label: 'Pending Tasks',
    description: 'Number of pending cluster tasks',
    keywords: ['pending tasks', 'pending', 'tasks'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.es60.cluster.health.number_of_pending_tasks',
    formatter: (v) => `**Pending Tasks:** ${v ?? 'N/A'}`,
  },
  {
    id: 'cluster.excludedDI',
    label: 'Excluded DI',
    description: 'DI numbers excluded from allocation',
    keywords: ['excluded di', 'excluded', 'di excluded', 'allocation exclude'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.es60.cluster.settings.transient.cluster.routing.allocation.exclude.di_number',
    formatter: (v) => `**Excluded DI:** ${v || 'None'}`,
  },
  {
    id: 'cluster.excludedNodes',
    label: 'Excluded Nodes (IPs)',
    description: 'Node IPs excluded from allocation',
    keywords: ['excluded nodes', 'excluded ips', 'excluded ip'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.es60.cluster.settings.transient.cluster.routing.allocation.exclude._ip',
    formatter: (v) => {
      if (!v || v === '') return '**Excluded Nodes:** None';
      const count = v.split(',').length;
      return `**Excluded Nodes:** ${count} node(s) — ${v}`;
    },
  },
  {
    id: 'cluster.allFields',
    label: 'Cluster Info (All)',
    description: 'Full cluster info summary',
    keywords: ['cluster info', 'all cluster', 'cluster summary', 'cluster overview'],
    category: 'cluster-info',
    queryId: 'CLUSTER_INFO',
    fieldPath: 'domain.es60',
    formatter: (es60) => {
      if (!es60) return '⚠️ No cluster info available.';
      const h = es60.cluster?.health || {};
      const fs = es60.cluster?.stats?.nodes?.fs || {};
      const nodes = es60.cluster?.stats?.nodes?.count || {};
      return [
        '## 🔍 Cluster Info',
        '| Metric | Value |',
        '|--------|-------|',
        `| ES Health | ${h.status || 'unknown'} |`,
        `| Active Shards | ${h.active_shards ?? 'N/A'} |`,
        `| Primary Shards | ${h.active_primary_shards ?? 'N/A'} |`,
        `| Relocating | ${h.relocating_shards ?? 'N/A'} |`,
        `| Initializing | ${h.initializing_shards ?? 'N/A'} |`,
        `| Unassigned | ${h.unassigned_shards ?? 'N/A'} |`,
        `| Data Nodes | ${h.number_of_data_nodes ?? 'N/A'} |`,
        `| Master Nodes | ${nodes.master ?? 'N/A'} |`,
        `| Coordinator Nodes | ${nodes.coordinating_only ?? 'N/A'} |`,
        `| Disk | ${fs.available || 'N/A'} free / ${fs.total || 'N/A'} total |`,
        `| Pending Tasks | ${h.number_of_pending_tasks ?? 'N/A'} |`,
      ].join('\n');
    },
  },
];
